// User-facing copy — README.md "Copy rules" and per-rule text/fix templates.
// Every string the package emits is built here so the wording rules (whole
// units, lowercase mid-sentence channel names, no exclamation marks) are
// enforced in one place.

import type { Product } from '@roomtreatment/av-catalog';
import type { Channel, ChannelGroup, RuleId } from './types';

/** Issue.id — "Stable across re-runs of the same build:
 *  `${rule}:${subjects.sort().join(',')}`." */
export function issueId(rule: RuleId, subjects: string[]): string {
  return `${rule}:${[...subjects].sort().join(',')}`;
}

export const CHANNEL_NAME: Record<Channel, string> = {
  FL: 'Front left',
  FR: 'Front right',
  C: 'Center',
  SL: 'Surround left',
  SR: 'Surround right',
  SBL: 'Rear surround left',
  SBR: 'Rear surround right',
  TFL: 'Front height left',
  TFR: 'Front height right',
  TML: 'Middle height left',
  TMR: 'Middle height right',
  TRL: 'Rear height left',
  TRR: 'Rear height right',
  LFE: 'Subwoofer',
};

/** Channel name, lowercase mid-sentence — "Copy rules". */
export function channelLower(channel: Channel): string {
  return lowerFirst(CHANNEL_NAME[channel]);
}

export function lowerFirst(s: string): string {
  return s.length ? s[0].toLowerCase() + s.slice(1) : s;
}

export function upperFirst(s: string): string {
  return s.length ? s[0].toUpperCase() + s.slice(1) : s;
}

/** Joins two channel names naturally: "Front left and front right". */
export function joinTwo(a: string, b: string): string {
  return `${a} and ${lowerFirst(b)}`;
}

/** Natural-language list join: "a", "a and b", "a, b and c". */
export function joinList(items: string[]): string {
  if (items.length === 0) return '';
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

export const GROUP_LABEL: Record<ChannelGroup, string> = {
  front: 'front',
  center: 'center',
  surround: 'surround',
  surround_back: 'rear surround',
  height1: 'height',
  height2: 'height',
  height3: 'height',
  height4: 'height',
  wide: 'front wide',
};

/** `brand model` — "Copy rules". */
export function productName(p: Product): string {
  return `${p.brand} ${p.model}`;
}

export function pluralize(n: number, noun: string, plural = `${noun}s`): string {
  return n === 1 ? noun : plural;
}

export function countNoun(n: number, noun: string, plural = `${noun}s`): string {
  return `${n} ${pluralize(n, noun, plural)}`;
}

// ── Numbers — "Numbers: whole dB, whole W, whole Ω, cm below 1 m, m to 0.1
//    otherwise, whole degrees." ─────────────────────────────────────────

export function fmtDb(v: number): number {
  return Math.round(v);
}

export function fmtWatts(v: number): number {
  return Math.round(v);
}

export function fmtOhms(v: number): number {
  return Math.round(v);
}

export function fmtDeg(v: number): number {
  return Math.round(v);
}

/** cm below 1 m, m to 0.1 otherwise. Returns the already-unit-suffixed
 *  string so callers don't have to branch. */
export function fmtLength(meters: number): string {
  if (meters < 1) return `${Math.round(meters * 100)} cm`;
  return `${meters.toFixed(1)} m`;
}

/** Always metres to 0.1 — used for cable run lengths, which are always
 *  reported in metres per the checklist copy examples ("5.5 m") even when a
 *  particular run happens to be under a metre. */
export function fmtMeters(meters: number): string {
  return `${meters.toFixed(1)} m`;
}

/** Round a length UP to the next 0.5 m — "Runs" formula. */
export function roundUpHalfMeter(meters: number): number {
  return Math.ceil(meters / 0.5) * 0.5;
}

/** Round power UP to the next 10 W — PWR-02. */
export function roundUpTenWatts(w: number): number {
  return Math.ceil(w / 10) * 10;
}
